import { Badge, Box, Divider, FormControlLabel, Menu, Slider, Switch, Typography } from "@mui/material";
import { themeColors } from "../styles/theme";
import { PrimaryButton } from "./Home";
import { Dispatch, MouseEvent, SetStateAction, useState } from "react";

interface FiltersAndTabSelectorProps {
    tab: 'list' | 'map';
    setTab: (value: 'list' | 'map') => void;
    handleFilterMenuClick: (event: MouseEvent<HTMLElement>) => void;
    filterMenuAnchorEl: HTMLElement | null;
    activeFilters: number;
    handleFilterMenuClose: () => void;
    sortBy: 'distance' | 'rating';
    setSortBy: Dispatch<SetStateAction<'distance' | 'rating'>>;
    sortDirection: 'asc' | 'desc';
    setSortDirection: Dispatch<SetStateAction<'asc' | 'desc'>>;
    customRadius: number;
    setCustomRadius: Dispatch<SetStateAction<number>>;
    handleApplyFilters: () => void;
    hideNoGardens: boolean;
    setHideNoGardens: Dispatch<SetStateAction<boolean>>;
    loading: boolean;
}

export const FiltersAndTabSelector = ({
    tab,
    setTab,
    handleFilterMenuClick,
    filterMenuAnchorEl,
    activeFilters,
    handleFilterMenuClose,
    sortBy,
    setSortBy,
    sortDirection,
    setSortDirection,
    customRadius,
    setCustomRadius,
    handleApplyFilters,
    hideNoGardens,
    setHideNoGardens,
    loading
}: FiltersAndTabSelectorProps
) => {
    const [filterOpen, setFilterOpen] = useState(false);

    return (
    <Box
        sx={{
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'flex-start',
            backgroundColor: 'transparent',
            marginTop: '26px',
            marginLeft: '5%',
            width: '90%',
        }}
    >
        {!loading && <><Badge
            badgeContent={activeFilters}
            color="primary"
            invisible={activeFilters === 0}
            anchorOrigin={{
                vertical: 'top',
                horizontal: 'left',
            }}
        >
            <PrimaryButton
                sx={{
                    width: '20%',
                    minWidth: '100px',
                    mr: 2
                }}
                onClick={handleFilterMenuClick}
            >
                FILTERS
            </PrimaryButton>
        </Badge>
        <PrimaryButton
            sx={{
                width: '20%',
                minWidth: '100px',
            }}
            onClick={() => {
                setTab(tab === 'list' ? 'map' : 'list');
            }}
        >
            {tab === 'list' ? 'MAP VIEW' : 'LIST VIEW'}
        </PrimaryButton></>}

        {Boolean(filterMenuAnchorEl) && <Menu
        anchorEl={filterMenuAnchorEl}
        open={Boolean(filterMenuAnchorEl)}
        onClose={handleFilterMenuClose}
        PaperProps={{
            elevation: 3,
            sx: {
                width: '100%',
                padding: '16px',
                borderRadius: 4,
                mt: 1,
                backgroundColor: themeColors.lightBlue,
                marginTop: '24px',
            }
        }}
    >
        <Typography variant="h6" sx={{
            mb: 2,
            fontFamily: "'DM Sans', sans-serif",
            color: themeColors.darkBackground
        }}>
            Sort & Filter
        </Typography>

        <Typography variant="subtitle2" sx={{
            fontWeight: 500,
            mb: 1,
            color: themeColors.darkBackground
        }}>
            Sort by:
        </Typography>

        <Box sx={{ mb: 2 }}>
            <PrimaryButton
                variant={sortBy === 'distance' ? 'contained' : 'outlined'}
                size="small"
                onClick={() => setSortBy('distance')}
                sx={{
                    fontSize: '1rem',
                    mr: 1,
                    padding: '4px',
                    width: '45%',
                    backgroundColor: sortBy === 'distance' ? themeColors.blue : 'transparent',
                    borderColor: themeColors.blue,
                    color: sortBy === 'distance' ? 'white' : themeColors.blue,
                    '&:hover': {
                        backgroundColor: sortBy === 'distance' ? themeColors.blue : 'rgba(253, 128, 0, 0.1)'
                    }
                }}
            >
                DISTANCE
            </PrimaryButton>
            <PrimaryButton
                variant={sortBy === 'rating' ? 'contained' : 'outlined'}
                size="small"
                onClick={() => setSortBy('rating')}
                sx={{
                    fontSize: '1rem',
                    padding: '4px',
                    width: '45%',
                    backgroundColor: sortBy === 'rating' ? themeColors.blue : 'transparent',
                    borderColor: themeColors.blue,
                    color: sortBy === 'rating' ? 'white' : themeColors.blue,
                    '&:hover': {
                        backgroundColor: sortBy === 'rating' ? themeColors.blue : 'rgba(253, 128, 0, 0.1)'
                    }
                }}
            >
                SUNNYNESS
            </PrimaryButton>
        </Box>

        {/* New sort direction toggle */}
        <Typography variant="subtitle2" sx={{
            fontWeight: 500,
            mb: 1,
            color: themeColors.darkBackground
        }}>
            Sort direction:
        </Typography>

        <Box sx={{ mb: 2 }}>
            <PrimaryButton
                variant={sortDirection === 'asc' ? 'contained' : 'outlined'}
                size="small"
                onClick={() => setSortDirection('asc')}
                sx={{
                    mr: 1,
                    fontSize: '0.8rem',
                    padding: '4px',
                    width: '45%',
                    backgroundColor: sortDirection === 'asc' ? themeColors.blue : 'transparent',
                    borderColor: themeColors.blue,
                    color: sortDirection === 'asc' ? 'white' : themeColors.blue,
                    '&:hover': {
                        backgroundColor: sortDirection === 'asc' ? themeColors.blue : 'rgba(253, 128, 0, 0.1)'
                    }
                }}
            >
                {sortBy === 'distance' ? 'CLOSEST FIRST' : 'BEST FIRST'}
            </PrimaryButton>
            <PrimaryButton
                variant={sortDirection === 'desc' ? 'contained' : 'outlined'}
                size="small"
                onClick={() => setSortDirection('desc')}
                sx={{
                    fontSize: '0.8rem',
                    padding: '4px',
                    width: '45%',
                    backgroundColor: sortDirection === 'desc' ? themeColors.blue : 'transparent',
                    borderColor: themeColors.blue,
                    color: sortDirection === 'desc' ? 'white' : themeColors.blue,
                    '&:hover': {
                        backgroundColor: sortDirection === 'desc' ? themeColors.blue : 'rgba(253, 128, 0, 0.1)'
                    }
                }}
            >
                {sortBy === 'distance' ? 'FARTHEST FIRST' : 'WORST FIRST'}
            </PrimaryButton>
        </Box>

        <Divider sx={{ my: 2 }} />

        <Typography variant="subtitle2" sx={{
            fontWeight: 500,
            mb: 1,
            color: themeColors.darkBackground
        }}>
            Filters:
        </Typography>

        <FormControlLabel
            control={
                <Switch
                    checked={hideNoGardens}
                    onChange={(e) => setHideNoGardens(e.target.checked)}
                    color="primary"
                    sx={{
                        '& .MuiSwitch-switchBase.Mui-checked': {
                            color: themeColors.orange,
                        },
                        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                            backgroundColor: themeColors.orange,
                        }
                    }}
                />
            }
            label="Show only pubs with gardens"
            sx={{ mb: 2 }}
        />

        <Typography variant="subtitle2" sx={{
            fontWeight: 500,
            mb: 1,
            color: themeColors.darkBackground
        }}>
            Search radius: {customRadius}m
        </Typography>

        <Box sx={{ px: 1, mb: 3 }}>
            <Slider
                value={customRadius}
                onChange={(e, newValue) => setCustomRadius(newValue as number)}
                min={500}
                max={5000}
                step={100}
                marks={[
                    { value: 500, label: '500m' },
                    { value: 5000, label: '5km' }
                ]}
                sx={{
                    color: themeColors.mainYellow,
                    '& .MuiSlider-thumb': {
                        height: 20,
                        width: 20,
                    }
                }}
            />
        </Box>

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
            <PrimaryButton
                variant="text"
                onClick={handleFilterMenuClose}
                sx={{
                    mr: 1,
                    color: themeColors.darkBackground,
                    padding: '4px',
                    width: '40%',
                }}
            >
                CANCEL
            </PrimaryButton>
            <PrimaryButton
                variant="contained"
                onClick={handleApplyFilters}
                sx={{
                    '&:hover': {
                        backgroundColor: themeColors.orange,
                        opacity: 0.9
                    },
                    color: 'black',
                    fontWeight: 500,
                    padding: '4px',
                    width: '40%',
                }}
            >
                APPLY
            </PrimaryButton>
        </Box>
    </Menu>}
    </Box>)
}